import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { formatDate, formatTime, isStaticVideoChannel } from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import { Banner, Button, Eyebrow, Icon, Pill, Skeleton, useToast } from '../../ds/components';
import { api, errorMessage } from '../../lib/api';
import { queryRoots } from '../../lib/events';
import { useWorkspace } from '../../lib/session';
import { ContactCard, type ContactCardRow } from '../queue/ContactCard';
import { shortDay } from '../tasks/dates';
import {
  isOverWithoutOutcome,
  pillVariant,
  useAppointment,
  type AppointmentDetail,
  type Delivery,
} from './api';
import { CancelDialog } from './CancelDialog';
import { OutcomeDialog } from './OutcomeDialog';
import { RescheduleDialog } from './RescheduleDialog';
import styles from './AppointmentDetailPage.module.css';

const DELIVERY_STATUS: Record<Delivery['status'], string> = {
  scheduled: 'geplant',
  sent: 'gesendet',
  delivered: 'zugestellt',
  opened: 'geöffnet',
  failed: 'fehlgeschlagen',
  cancelled: 'abgebrochen',
};

interface ContactHeader {
  header: { initials: string; lifecycle: { label: string }; dnc: boolean };
}

/** The contact's lifecycle for the card's role line („Kunde · Deal #2" / „Call gebucht · kein Deal"). */
function useContactHeader(contactId: string | null) {
  return useQuery({
    queryKey: [...queryRoots.contact, contactId ?? '', 'appointment-card'],
    queryFn: () => api<ContactHeader>(`/contacts/${contactId ?? ''}`),
    enabled: contactId !== null,
    staleTime: 60_000,
  });
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.charAt(0) ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';
  return `${first}${last}`.toUpperCase();
}

function contactRows(appointment: AppointmentDetail): ContactCardRow[] {
  const rows: ContactCardRow[] = [];
  if (appointment.contactPhone) rows.push({ label: 'Telefon', value: appointment.contactPhone });
  else if (appointment.contactEmail) rows.push({ label: 'E-Mail', value: appointment.contactEmail });
  rows.push({ label: 'Betreuer', value: appointment.advisor.shortName });
  if (appointment.deal) rows.push({ label: 'Deal', value: `Deal #${appointment.deal.number}` });
  return rows;
}

/** „24.09., 16:00" for entries with a time of day, „21.09." for booked/rescheduled/cancelled lines. */
function historyDate(entry: AppointmentDetail['history'][number]): string {
  const day = shortDay(entry.at.slice(0, 10));
  return entry.kind === 'outcome' || entry.kind === 'reminder' || entry.kind === 'room_opened'
    ? `${day}, ${formatTime(entry.at)}`
    : day;
}

/** „Beitreten" opens 10 minutes before the start (or whenever the host's room is already open). */
const JOIN_LEAD_MS = 10 * 60_000;

/**
 * The room is entered with a document navigation (camera/microphone are allowed for that path only); an
 * appointment booked before its room existed gets it on first access.
 */
async function enterRoom(appointment: AppointmentDetail): Promise<void> {
  const meetingId =
    appointment.meeting?.id ?? (await api<{ id: string }>(`/meetings/by-appointment/${appointment.id}`)).id;
  window.location.assign(`/calendar/room/${meetingId}`);
}

/**
 * `/calendar/appointments/:id` — Queue 08/09: the appointment card with type, status pill, time text,
 * details (M5: recording consent, participation), history and deliveries, the contact card, and the actions
 * Absagen / Verschieben / Raum öffnen / Beitreten (video room, M5) / Ergebnis erfassen (with the „Termin
 * vorbei ohne Ergebnis" banner) plus the ICS download.
 */
export function AppointmentDetailPage() {
  const params = useParams();
  const id = params['id'] ?? null;
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const { toast } = useToast();
  const appointment = useAppointment(id);
  const [outcomeOpen, setOutcomeOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const contactHeader = useContactHeader(appointment.data?.contact?.id ?? null);
  const workspace = useWorkspace();
  const [entering, setEntering] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  // ?outcome=1 / ?reschedule=1 open the flow directly (from the task row's „Erfassen" and the queue).
  useEffect(() => {
    if (!appointment.data) return;
    const flags = { outcome: search.get('outcome') === '1', reschedule: search.get('reschedule') === '1' };
    if (!flags.outcome && !flags.reschedule) return;
    if (flags.outcome && isOverWithoutOutcome(appointment.data)) setOutcomeOpen(true);
    if (flags.reschedule && appointment.data.status === 'booked') setRescheduleOpen(true);
    const next = new URLSearchParams(search);
    next.delete('outcome');
    next.delete('reschedule');
    setSearch(next, { replace: true });
  }, [appointment.data, search, setSearch]);

  if (id === null) return null;

  if (appointment.isError) {
    return (
      <div className={styles.page}>
        <div className={styles.crumb}>
          <Link to="/calendar" className={styles.crumbLink}>
            Beratung
          </Link>{' '}
          / Termine
        </div>
        <Banner
          tone="danger"
          actions={
            <Button variant="secondary" size="small" onClick={() => void navigate('/calendar')}>
              Zur Beratung
            </Button>
          }
        >
          {errorMessage(appointment.error)}
        </Banner>
      </div>
    );
  }

  if (appointment.isPending) {
    return (
      <div className={styles.page} aria-busy="true" aria-label="Wird geladen">
        <div className={styles.header}>
          <div>
            <Skeleton width={110} height={8} />
            <div className={styles.skeletonTitle}>
              <Skeleton width={300} height={26} />
            </div>
            <Skeleton width={360} />
          </div>
        </div>
        <div className={styles.grid}>
          <div className={styles.column}>
            <Skeleton variant="block" height={240} />
            <Skeleton variant="block" height={160} />
          </div>
          <Skeleton variant="block" height={220} />
        </div>
      </div>
    );
  }

  const data = appointment.data;
  const over = isOverWithoutOutcome(data);
  const upcoming = data.status === 'booked' && !over;
  const contactName = data.contact?.name ?? data.guestName ?? 'Gast';
  const lastHistory = data.history.length - 1;
  const lifecycleLabel =
    contactHeader.data?.header.lifecycle.label ?? (data.deal ? 'Interessent' : 'Call gebucht');
  const roleLine = `${lifecycleLabel} · ${data.deal ? `Deal #${data.deal.number}` : 'kein Deal'}`;
  const video = data.channel === 'video';
  // Google Meet / Teams / Zoom: the advisor's personal link is the location; there is no DAMOKLES room.
  const staticLink = isStaticVideoChannel(data.channel) ? data.location : null;
  // „Raum öffnen": the server decides for an existing room; before the room exists the advisor of a
  // booked video appointment may open it (the room is created on that first access).
  const canOpen =
    upcoming &&
    (data.meeting
      ? data.meeting.canOpen
      : video && data.advisor.id === workspace.user.id && workspace.modules.includes('video_consulting'));
  const canJoin =
    upcoming &&
    ((data.meeting?.canJoin ?? false) || (canOpen && now >= Date.parse(data.startsAt) - JOIN_LEAD_MS));
  const reviewHref = !over ? (data.meeting?.reviewHref ?? null) : null;

  async function openRoom() {
    setEntering(true);
    try {
      await enterRoom(data);
    } catch (error) {
      setEntering(false);
      toast({ message: errorMessage(error) });
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <div className={styles.crumb}>
            <Link to="/calendar" className={styles.crumbLink}>
              Beratung
            </Link>{' '}
            / Termine
          </div>
          <div className={styles.titleRow}>
            <h1 className={styles.title}>{data.sequenceTitle ?? data.eventType.name}</h1>
            <Pill variant={pillVariant(data)}>{data.pill}</Pill>
          </div>
          <div className={styles.subtitle}>{data.timeRangeText}</div>
        </div>
        <div className={styles.actions}>
          {upcoming ? (
            <>
              <Button variant="secondary" onClick={() => setCancelOpen(true)}>
                Absagen
              </Button>
              <Button variant="secondary" onClick={() => setRescheduleOpen(true)}>
                Verschieben
              </Button>
            </>
          ) : null}
          {canOpen ? (
            <Button variant="secondary" disabled={entering} onClick={() => void openRoom()}>
              Raum öffnen
            </Button>
          ) : null}
          {canJoin ? (
            <Button loading={entering} onClick={() => void openRoom()}>
              Beitreten
            </Button>
          ) : null}
          {reviewHref ? (
            <Button variant="secondary" onClick={() => void navigate(reviewHref)}>
              Rückblick
            </Button>
          ) : null}
          {over ? <Button onClick={() => setOutcomeOpen(true)}>Ergebnis erfassen</Button> : null}
        </div>
      </div>

      {over ? (
        <div className={styles.warning} role="alert">
          Der Termin ist vorbei, ein Ergebnis fehlt.{' '}
          {data.wrapUpTaskId
            ? 'Die Task „Termin nachbereiten“ steht in Ihrer Queue.'
            : 'Erfassen Sie das Ergebnis, damit der Kontakt einen nächsten Schritt bekommt.'}
        </div>
      ) : null}
      {data.status === 'cancelled' ? (
        <Banner tone="neutral">
          Abgesagt
          {data.cancelledBy === 'customer'
            ? ' durch den Kunden'
            : data.cancelledBy === 'advisor'
              ? ' durch den Berater'
              : ''}
          {data.cancellationReason ? ` · ${data.cancellationReason}` : ''}
        </Banner>
      ) : null}
      {data.status === 'rescheduled' && data.rescheduledToAppointmentId ? (
        <Banner
          tone="neutral"
          actions={
            <Link to={`/calendar/appointments/${data.rescheduledToAppointmentId}`}>Neuen Termin öffnen</Link>
          }
        >
          Dieser Termin wurde verschoben.
        </Banner>
      ) : null}

      <div className={styles.grid}>
        <div className={styles.column}>
          <div className={styles.card}>
            <Eyebrow>Details</Eyebrow>
            <dl className={styles.rows}>
              <div className={styles.row}>
                <dt>Ereignistyp</dt>
                <dd>{data.eventType.name}</dd>
              </div>
              <div className={styles.row}>
                <dt>Gebucht über</dt>
                <dd>{data.bookedViaLabel}</dd>
              </div>
              <div className={styles.row}>
                <dt>Setter</dt>
                <dd>{data.setter?.shortName ?? '—'}</dd>
              </div>
              {staticLink ? (
                <div className={styles.row}>
                  <dt>Ort</dt>
                  <dd>
                    {data.channelLabel} ·{' '}
                    <a href={staticLink} target="_blank" rel="noreferrer" className={styles.meetingLink}>
                      {staticLink}
                      <Icon icon={ExternalLink} size={16} />
                    </a>
                  </dd>
                </div>
              ) : data.location ? (
                <div className={styles.row}>
                  <dt>Ort</dt>
                  <dd>{data.location}</dd>
                </div>
              ) : null}
              {!over ? (
                <div className={styles.row}>
                  <dt>Erinnerung</dt>
                  <dd>{data.reminderText ?? 'keine'}</dd>
                </div>
              ) : null}
              {video && !over ? (
                <div className={styles.row}>
                  <dt>Einwilligung Aufzeichnung</dt>
                  <dd>{data.recordingConsent ?? 'noch nicht erteilt'}</dd>
                </div>
              ) : null}
              {data.participation ? (
                <div className={styles.row}>
                  <dt>Teilnahme</dt>
                  <dd>{data.participation}</dd>
                </div>
              ) : null}
              {data.outcome ? (
                <div className={styles.row}>
                  <dt>Ergebnis</dt>
                  <dd>
                    {data.statusLabel}
                    {data.outcomeText ? ` · ${data.outcomeText}` : ''}
                    {data.outcomeRecordedAt ? ` · erfasst ${formatDate(data.outcomeRecordedAt)}` : ''}
                  </dd>
                </div>
              ) : null}
              {data.concern ? (
                <div className={styles.row}>
                  <dt>Anliegen</dt>
                  <dd>{data.concern}</dd>
                </div>
              ) : null}
            </dl>
            <div className={styles.cardFoot}>
              <a href={`/api/appointments/${data.id}/ics`} download="termin.ics" className={styles.icsLink}>
                ICS herunterladen
              </a>
            </div>
          </div>

          <div className={styles.card}>
            <Eyebrow>Verlauf</Eyebrow>
            <div className={styles.timeline}>
              {data.history.map((entry, index) => (
                <div key={`${entry.kind}-${entry.at}-${index}`} className={styles.timelineRow}>
                  <span
                    className={clsx(styles.dot, index === lastHistory && styles.dotAccent)}
                    aria-hidden="true"
                  />
                  <div className={styles.timelineText}>
                    <div className={styles.timelineTitle}>{entry.title}</div>
                    {entry.text ? <div className={styles.timelineSub}>{entry.text}</div> : null}
                  </div>
                  <span className={styles.timelineDate}>{historyDate(entry)}</span>
                </div>
              ))}
            </div>
          </div>

          {data.deliveries.length > 0 ? (
            <div className={styles.card}>
              <Eyebrow>Zustellungen</Eyebrow>
              <div className={styles.timeline}>
                {data.deliveries.map((delivery) => (
                  <div key={delivery.id} className={styles.timelineRow}>
                    <span
                      className={clsx(
                        styles.dot,
                        delivery.status === 'failed' && styles.dotDanger,
                        (delivery.status === 'sent' ||
                          delivery.status === 'delivered' ||
                          delivery.status === 'opened') &&
                          styles.dotSuccess,
                      )}
                      aria-hidden="true"
                    />
                    <div className={styles.timelineText}>
                      <div className={styles.timelineTitle}>{delivery.label}</div>
                      <div className={styles.timelineSub}>
                        {DELIVERY_STATUS[delivery.status]}
                        {delivery.error ? ` · ${delivery.error}` : ''}
                      </div>
                    </div>
                    <span className={styles.timelineDate}>
                      {shortDay((delivery.sentAt ?? delivery.sendAt).slice(0, 10))},{' '}
                      {formatTime(delivery.sentAt ?? delivery.sendAt)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className={styles.column}>
          <ContactCard
            name={contactName}
            initials={contactHeader.data?.header.initials ?? initialsOf(contactName)}
            roleLine={roleLine}
            rows={contactRows(data)}
            href={data.contact ? `/contacts/${data.contact.id}` : undefined}
          />
        </div>
      </div>

      <OutcomeDialog
        open={outcomeOpen}
        appointment={data}
        onClose={() => setOutcomeOpen(false)}
        onDone={() => {
          setOutcomeOpen(false);
          toast({ message: 'Termin-Ergebnis gespeichert' });
        }}
        onReschedule={() => {
          setOutcomeOpen(false);
          setRescheduleOpen(true);
        }}
      />
      <RescheduleDialog
        open={rescheduleOpen}
        appointment={data}
        onClose={() => setRescheduleOpen(false)}
        onRescheduled={(created) => {
          setRescheduleOpen(false);
          toast({ message: `Verschoben auf ${created.timeRangeText}` });
          void navigate(`/calendar/appointments/${created.id}`, { replace: true });
        }}
      />
      <CancelDialog
        open={cancelOpen}
        appointment={data}
        onClose={() => setCancelOpen(false)}
        onCancelled={() => {
          setCancelOpen(false);
          toast({ message: 'Termin abgesagt' });
        }}
      />
    </div>
  );
}
